/** Decodes CSV bytes: UTF-8 (with or without BOM), UTF-16 BOM, else Windows-1252 (what Excel on Windows writes). */
export function decodeCsvBytes(buf: ArrayBuffer): { text: string; encoding: string } {
  const b = new Uint8Array(buf);
  if (b[0] === 0xff && b[1] === 0xfe) return { text: new TextDecoder("utf-16le").decode(b.subarray(2)), encoding: "UTF-16" };
  if (b[0] === 0xfe && b[1] === 0xff) return { text: new TextDecoder("utf-16be").decode(b.subarray(2)), encoding: "UTF-16" };
  try {
    const t = new TextDecoder("utf-8", { fatal: true }).decode(b);
    return { text: t.replace(/^﻿/, ""), encoding: "UTF-8" };
  } catch {
    return { text: new TextDecoder("windows-1252").decode(b), encoding: "Windows-1252" };
  }
}

/** Picks the delimiter that gives the most consistent column count over the first lines (quotes respected). */
export function detectDelimiter(text: string): string {
  const candidates = [",", ";", "\t", "|"];
  const sample = text.slice(0, 20000);
  let best = ",", bestScore = -1;
  for (const d of candidates) {
    const rows = parseDelimited(sample, d).slice(0, 30);
    if (rows.length === 0) continue;
    const counts = rows.map(r => r.length);
    const mode = counts.sort((a, b) => a - b)[Math.floor(counts.length / 2)];
    if (mode < 2) continue;
    const consistent = counts.filter(c => c === mode).length / counts.length;
    const score = consistent * 100 + mode;
    if (score > bestScore) { bestScore = score; best = d; }
  }
  return best;
}

/** RFC-4180 style parser: quoted fields may contain delimiters, quotes ("") and line breaks. */
export function parseDelimited(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === delimiter) { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field); field = ""; rows.push(row); row = [];
    } else field += ch;
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter(r => r.length > 1 || r[0] !== "");
}

export function toCsvText(rows: string[][], delimiter = ",", bom = false): string {
  const esc = (c: string) => (c.includes(delimiter) || /["\r\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
  return (bom ? "﻿" : "") + rows.map(r => r.map(esc).join(delimiter)).join("\r\n");
}

/** Plain numbers become numbers (so Excel can sum them); IDs with leading zeros, long digit strings and anything else stay text. */
export function typedCell(v: string): string | number {
  const t = v.trim();
  if (/^-?(0|[1-9]\d{0,14})(\.\d+)?$/.test(t) && t.length <= 15) return Number(t);
  return v;
}

/**
 * CSV for export: quotes cells that need it, and defuses spreadsheet formulas.
 * A cell starting with = + - @ (or tab/CR) would run as a formula when opened in Excel or Sheets, so it gets a leading
 * apostrophe. Real negative numbers like -5 are left alone.
 */
const PHONE_LIKE = /^\+\d[\d ()./-]{5,24}$/;

export function safeCsvText(rows: (string | number)[][], delimiter = ",", bom = true): string {
  const protect = (v: string | number) => {
    const s = String(v ?? "");
    if (typeof v === "number" || /^-?\d+([.,]\d+)?$/.test(s)) return s;
    // International phone numbers ("+1 555 0100", "+44 (0)20 7946 0958") are text, not formulas: no apostrophe.
    if (PHONE_LIKE.test(s)) return s;
    return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  };
  return toCsvText(rows.map((r) => r.map(protect)), delimiter, bom);
}
